<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Modal from 'dashboard/components/Modal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  attribute: { type: Object, default: () => ({}) },
  usage: {
    type: Object,
    default: () => ({
      automation_rules: [],
      pricing_rules: [],
      pricing_items: [],
      procedures: [],
    }),
  },
});
const emit = defineEmits(['confirm', 'close', 'update:show']);
const { t } = useI18n();
const route = useRoute();

const accountId = computed(() => route.params.accountId);

const confirmInput = ref('');

const localShow = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});

const attributeName = computed(
  () => props.attribute.attribute_display_name || ''
);

const isConfirmValid = computed(
  () => confirmInput.value.trim() === attributeName.value.trim()
);

const hasUsages = computed(() => {
  const u = props.usage;
  return (
    u.automation_rules.length > 0 ||
    u.pricing_rules.length > 0 ||
    u.pricing_items.length > 0 ||
    u.procedures.length > 0
  );
});

function closeModal() {
  confirmInput.value = '';
  emit('close');
}

function onConfirm() {
  if (!isConfirmValid.value) return;
  emit('confirm');
}
</script>

<template>
  <Modal v-model:show="localShow" :on-close="closeModal">
    <div class="modal-header">
      <h2>
        {{
          $t('ATTRIBUTES_MGMT.DELETE.CONFIRM.TITLE', {
            attributeName,
          })
        }}
      </h2>

      <!-- Usage warnings -->
      <div v-if="hasUsages" class="flex flex-col gap-3 w-full mt-2">
        <p class="text-sm text-n-ruby-11 font-medium">
          {{ $t('ATTRIBUTES_MGMT.DELETE.USAGE.WARNING') }}
        </p>

        <!-- Automation Rules -->
        <div
          v-if="usage.automation_rules.length"
          class="rounded-lg border border-n-slate-6 p-3"
        >
          <p class="text-sm font-medium text-n-slate-12 mb-1">
            {{
              $t('ATTRIBUTES_MGMT.DELETE.USAGE.AUTOMATION_RULES_TITLE', {
                count: usage.automation_rules.length,
              })
            }}
          </p>
          <ul class="list-disc pl-4 text-sm text-n-slate-11">
            <li v-for="rule in usage.automation_rules" :key="rule.id">
              <router-link
                :to="`/app/accounts/${accountId}/settings/automation/edit/${rule.id}`"
                class="text-n-blue-11 hover:underline"
              >
                {{ rule.name }}
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Pricing Rules -->
        <div
          v-if="usage.pricing_rules.length"
          class="rounded-lg border border-n-slate-6 p-3"
        >
          <p class="text-sm font-medium text-n-slate-12 mb-1">
            {{
              $t('ATTRIBUTES_MGMT.DELETE.USAGE.PRICING_RULES_TITLE', {
                count: usage.pricing_rules.length,
              })
            }}
          </p>
          <ul class="list-disc pl-4 text-sm text-n-slate-11">
            <li v-for="rule in usage.pricing_rules" :key="rule.id">
              <router-link
                :to="`/app/accounts/${accountId}/settings/planning/pricing/plans/${rule.pricing_plan_id}/rules/${rule.id}`"
                class="text-n-blue-11 hover:underline"
              >
                {{ rule.pricing_item_name || `#${rule.id}` }}
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Pricing Items -->
        <div
          v-if="usage.pricing_items.length"
          class="rounded-lg border border-n-slate-6 p-3"
        >
          <p class="text-sm font-medium text-n-slate-12 mb-1">
            {{
              $t('ATTRIBUTES_MGMT.DELETE.USAGE.PRICING_ITEMS_TITLE', {
                count: usage.pricing_items.length,
              })
            }}
          </p>
          <ul class="list-disc pl-4 text-sm text-n-slate-11">
            <li v-for="item in usage.pricing_items" :key="item.id">
              <router-link
                :to="`/app/accounts/${accountId}/settings/planning/pricing/price-list`"
                class="text-n-blue-11 hover:underline"
              >
                {{ item.name }}
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Procedures -->
        <div
          v-if="usage.procedures.length"
          class="rounded-lg border border-n-slate-6 p-3"
        >
          <p class="text-sm font-medium text-n-slate-12 mb-1">
            {{
              $t('ATTRIBUTES_MGMT.DELETE.USAGE.PROCEDURES_TITLE', {
                count: usage.procedures.length,
              })
            }}
          </p>
          <ul class="list-disc pl-4 text-sm text-n-slate-11">
            <li v-for="proc in usage.procedures" :key="proc.procedure_name">
              <router-link
                :to="`/app/accounts/${accountId}/captain/${proc.agent_bot_id}/scenarios`"
                class="text-n-blue-11 hover:underline"
              >
                {{ proc.procedure_name }}
              </router-link>
              <span class="text-n-slate-9">({{ proc.agent_name }})</span>
            </li>
          </ul>
        </div>
      </div>

      <!-- Default message when no usages -->
      <p v-else>
        {{ $t('ATTRIBUTES_MGMT.DELETE.CONFIRM.MESSAGE') }}
      </p>
    </div>
    <form class="w-full px-8 pb-8" @submit.prevent="onConfirm">
      <woot-input
        v-model="confirmInput"
        type="text"
        :placeholder="
          $t('ATTRIBUTES_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
            attributeName,
          })
        "
      />
      <div class="flex items-center justify-end gap-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('ATTRIBUTES_MGMT.DELETE.CONFIRM.NO')"
          @click.prevent="closeModal"
        />
        <NextButton
          ruby
          type="submit"
          :label="`${$t('ATTRIBUTES_MGMT.DELETE.CONFIRM.YES')} ${attributeName}`"
          :disabled="!isConfirmValid"
        />
      </div>
    </form>
  </Modal>
</template>
